import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import expedienteService from '../services/expedienteService';
import StatsCard from '../components/StatsCard';
import KPICard from '../components/KPICard';
import { 
  Target, 
  TrendingUp, 
  Star, 
  CheckCircle2, 
  XCircle, 
  AlertCircle, 
  FileText, 
  ClipboardList, 
  DoorOpen, 
  Pause, 
  BriefcaseIcon, 
  BadgeCheck 
} from 'lucide-react';
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const data = await expedienteService.getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  if (loading) {
    return (
      <div className="dashboard-container" data-testid="dashboard">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando estadísticas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container" data-testid="dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Panel de Control</h1>
          <p>Resumen de indicadores clave del sistema</p>
        </div>
      </div>

      {/* Stats Cards - Solo para Admin y Auditor */}
      {(user?.role === 'admin' || user?.role === 'auditor') && stats && (
        <>
          {/* KPI Cards - Indicadores Clave */}
          <div className="kpi-grid">
            <KPICard 
              title="Beneficiados"
              count={stats['Beneficiados']?.count || 0}
              value={formatCurrency(stats['Beneficiados']?.total_eur || 0)}
              color="success"
              icon={<Target size={24} />}
              description="Clientes con beneficios"
            />
            <KPICard 
              title="Aportantes"
              count={stats['Aportantes']?.count || 0}
              value={formatCurrency(stats['Aportantes']?.total_eur || 0)}
              color="primary"
              icon={<TrendingUp size={24} />}
              description="Clientes que aportan"
            />
            <KPICard 
              title=">65%"
              count={stats['>65%']?.count || 0}
              value={formatCurrency(stats['>65%']?.total_eur || 0)}
              color="premium"
              icon={<Star size={24} />}
              description="Avance superior al 65%"
            />
          </div>

          {/* Stats Cards Regulares */}
          <div className="stats-grid">
            <StatsCard 
              title="Activos"
              count={stats['Activos']?.count || 0}
              value={formatCurrency(stats['Activos']?.total_eur || 0)}
              color="green"
              icon={<CheckCircle2 size={24} />}
            />
            <StatsCard 
              title="Inactivos"
              count={stats['Inactivos']?.count || 0}
              value={formatCurrency(stats['Inactivos']?.total_eur || 0)}
              color="red"
              icon={<XCircle size={24} />}
            />
            <StatsCard 
              title="Morosos"
              count={stats['Morosos']?.count || 0}
              value={formatCurrency(stats['Morosos']?.total_eur || 0)}
              color="orange"
              icon={<AlertCircle size={24} />}
            />
            <StatsCard 
              title="Convenio"
              count={stats['Convenio']?.count || 0}
              value={formatCurrency(stats['Convenio']?.total_eur || 0)}
              color="blue"
              icon={<FileText size={24} />}
            />
            <StatsCard 
              title="Finiquito"
              count={stats['Finiquito']?.count || 0}
              value={formatCurrency(stats['Finiquito']?.total_eur || 0)}
              color="gray"
              icon={<ClipboardList size={24} />}
            />
            <StatsCard 
              title="Retirado"
              count={stats['Retirado']?.count || 0}
              value={formatCurrency(stats['Retirado']?.total_eur || 0)}
              color="gray"
              icon={<DoorOpen size={24} />}
            />
            <StatsCard 
              title="Retenido"
              count={stats['Retenido']?.count || 0}
              value={formatCurrency(stats['Retenido']?.total_eur || 0)}
              color="yellow"
              icon={<Pause size={24} />}
            />
            <StatsCard 
              title="CXPR"
              count={stats['CXPR']?.count || 0}
              value={formatCurrency(stats['CXPR']?.total_eur || 0)}
              color="purple"
              icon={<BriefcaseIcon size={24} />}
            />
            <StatsCard 
              title="Solventes"
              count={stats['Solventes']?.count || 0}
              value={formatCurrency(stats['Solventes']?.total_eur || 0)}
              color="green"
              icon={<BadgeCheck size={24} />}
            />
          </div>
        </>
      )}

      {/* Solo consulta - mensaje informativo */}
      {user?.role === 'consulta' && (
        <div className="info-message">
          <div className="info-icon">ℹ️</div>
          <div>
            <h3>Bienvenido al Sistema</h3>
            <p>Accede a "Expedientes" desde el menú lateral para consultar la información disponible.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
